import { type GeolocationConfig } from '@/types/config';
import type { Region } from '@/types/constants';
import { getConfig } from '@/utils/config/config';
import { getCountryCode } from '@/utils/region/get-region';
import type { Environment } from '@fanduel/core-config-contract';
import { type ProviderConfig } from '@fanduel/geolocation-sdk';
import type { Country } from '@fanduel/geolocation-sdk/lib/typescript/src/geolocation-web/types/common.types';

const resolveCountry = (geo: GeolocationConfig): Uppercase<Country> => {
    const country = geo.country ?? getCountryCode();

    return country.toLocaleUpperCase() as Uppercase<Country>;
};

/** Same resolution order as casino-game-client `useGeolocationHelpers.getEnvironment`. */
export const resolveGeocomplyEnvironment = (
    config: GeolocationConfig & { env: Environment['type']; name?: 'igtcert' | 'igtqa8' }
): ProviderConfig['environment'] => {
    if (config.env === 'devStack' || config.name) {
        return { type: 'devStack', name: config.name ?? 'igtcert' };
    }

    return { type: config.env } as unknown as ProviderConfig['environment'];
};

const buildProviderConfig = (
    geo: GeolocationConfig,
    params: { region: Region; sessionId: string; userId: string; userAuthToken: string; product?: string }
): ProviderConfig => {
    const config = getConfig();
    const { clientId, ...rest } = config.unifiedModules;

    const product = (params.product ?? process.env.PRODUCT) as 'casino' | 'poker';
    return {
        appDomain: {
            product,
            region: params.region.toUpperCase() as Uppercase<string>
        },
        appName: geo.serviceName,
        appVersion: process.env.BUILD_VERSION ?? 'unknown',
        clientAuthToken: clientId as unknown as string,
        installerKey: geo.installerKey,
        environment: resolveGeocomplyEnvironment({ ...geo, ...rest }),
        country: resolveCountry(geo),
        session: {
            userId: params.userId,
            userAuthToken: params.userAuthToken,
            sessionId: params.sessionId
        }
    };
};

export default buildProviderConfig;
